import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TermMatch } from "@/lib/clinicalTerms";

/**
 * One row of a complaint / diagnosis / test suggestion list: the name, the alias that matched, the code.
 * The doctor's own entries have a ✕ to take them out of their suggestions (a typo, a one-off).
 */
export default function TermSuggestion({ match, active, onPick, onForget, extra }: {
  match: TermMatch;
  active: boolean;
  onPick: () => void;
  onForget: () => void;
  /** Shown after the name, e.g. the duration typed with a complaint */
  extra?: string;
}) {
  const { term, alias } = match;
  return (
    <div className={cn("flex items-stretch border-b border-border/40 last:border-0", active ? "bg-primary/10 text-primary" : "hover:bg-accent text-foreground")}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); onPick(); }}
        className="flex-1 min-w-0 px-3 py-2 flex items-center gap-1.5 text-left text-sm"
      >
        <span className="truncate">{term.name}</span>
        {alias && <span className="shrink-0 text-xs text-muted-foreground">· {alias}</span>}
        {extra && <span className="shrink-0 text-muted-foreground">{extra}</span>}
        {term.code && <span className="shrink-0 ml-auto pl-2 font-mono text-[11px] text-muted-foreground">{term.code}</span>}
      </button>
      {term.own && (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onForget(); }}
          title="Remove from my suggestions"
          aria-label={`Remove ${term.name} from my suggestions`}
          className="touch-target px-2.5 text-muted-foreground hover:text-destructive"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
